import { useCallback, useMemo, useRef, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import type { AllocationName } from 'api';
import {
  PrimaryNavigationMenu,
  PrimaryNavigationMenuAction,
  PrimaryNavigationMenuItem,
  PrimaryNavigationMenuItemLeadingAction,
  PrimaryNavigationMenuSearch,
  PrimaryNavigationMenuShowMoreItem,
  SecondaryNavigationSelector,
} from 'components/ui';
import { useSearchableLimitedOptions } from 'components/ui/hooks';
import { useZoom } from 'context/ZoomContext';
import {
  SAVE_AS_NEW_VERSION_LABEL,
  VERSION_NAME_PREFIX,
} from 'pages/ValuationsAllocation/common/constants/valuations/valuations';

const VALUATION_VERSION_MENU_VISIBLE_ITEM_LIMIT = 8;
const VALUATION_VERSION_SELECTOR_MAX_WIDTH = 320;
const VALUATION_VERSION_SELECTOR_TEST_ID = 'secondary-navigation__selector-valuation-version-name';

interface ValuationVersionSelectorProps {
  disabled?: boolean;
  isCreateVersionDisabled?: boolean;
  onCreateVersion?: () => void;
  onOpenRenameDialog?: () => void;
  onVersionChange: (slug: string) => void;
  selectedVersion: AllocationName | undefined;
  versions: AllocationName[];
}

const getVersionPrefix = (version: AllocationName) => `${VERSION_NAME_PREFIX} ${version.version}`;

const getVersionLabel = (version: AllocationName) => {
  const prefix = getVersionPrefix(version);
  const versionName = version.name?.trim() ?? '';

  if (!versionName) {
    return prefix;
  }

  return versionName.toLowerCase().startsWith(prefix.toLowerCase()) ? versionName : `${prefix} - ${versionName}`;
};

const ValuationVersionSelector = ({
  disabled,
  isCreateVersionDisabled,
  onCreateVersion,
  onOpenRenameDialog,
  onVersionChange,
  selectedVersion,
  versions,
}: ValuationVersionSelectorProps) => {
  const { zoomLevel } = useZoom();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [positiveFeedbackKey, setPositiveFeedbackKey] = useState(0);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const {
    canShowFewerOptions,
    hasMoreOptions,
    hiddenOptionCount,
    resetSearchableOptions,
    searchValue,
    setSearchValue,
    setShowAllOptions,
    visibleOptions,
  } = useSearchableLimitedOptions({
    getSearchText: version => getVersionLabel(version),
    options: versions,
    visibleLimit: VALUATION_VERSION_MENU_VISIBLE_ITEM_LIMIT,
  });

  const handleOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
    resetSearchableOptions();
  }, [resetSearchableOptions]);

  const handleSelect = useCallback(
    (slug: string) => {
      handleClose();
      if (slug !== selectedVersion?.slug) {
        setPositiveFeedbackKey(key => key + 1);
      }
      onVersionChange(slug);
    },
    [handleClose, onVersionChange, selectedVersion?.slug]
  );

  const handleEditClick = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      handleClose();
      onOpenRenameDialog?.();
    },
    [handleClose, onOpenRenameDialog]
  );

  const handleCreateVersion = useCallback(() => {
    handleClose();
    onCreateVersion?.();
  }, [handleClose, onCreateVersion]);

  const selectorValue = useMemo(
    () => (selectedVersion ? getVersionLabel(selectedVersion) : ''),
    [selectedVersion]
  );

  return (
    <>
      <SecondaryNavigationSelector
        aria-expanded={Boolean(anchorEl)}
        aria-haspopup="menu"
        data-testid={VALUATION_VERSION_SELECTOR_TEST_ID}
        disabled={disabled}
        label="Version"
        onClick={handleOpen}
        positiveFeedbackKey={positiveFeedbackKey}
        sx={{ maxWidth: VALUATION_VERSION_SELECTOR_MAX_WIDTH }}
        value={selectorValue}
        valueTone="brand"
      />
      <PrimaryNavigationMenu
        anchorEl={anchorEl}
        disableAutoFocusItem
        horizontalPlacement="right"
        menuMaxWidth={320}
        menuMinWidth={180}
        menuWidth="content"
        onClose={handleClose}
        open={Boolean(anchorEl)}
        scrollHintItemLabelPlural="versions"
        scrollHintItemLabelSingular="version"
        zoomLevel={zoomLevel}
        TransitionProps={{ onEntered: () => searchInputRef.current?.focus() }}
        MenuListProps={{ disableListWrap: true }}>
        <PrimaryNavigationMenuSearch
          clearAriaLabel="Clear valuation version search"
          fullWidth
          inputRef={searchInputRef}
          onChange={event => setSearchValue(event.target.value)}
          onClear={() => setSearchValue('')}
          placeholder="Find a Version"
          value={searchValue}
        />
        {visibleOptions.map(version => {
          const isSelected = version.slug === selectedVersion?.slug;
          const versionId = String(version.id ?? version.slug);
          return (
            <PrimaryNavigationMenuItem
              data-testid={`secondary-navigation__selector-valuation-version-option-${versionId}`}
              endIcon={isSelected && onOpenRenameDialog ? (
                <PrimaryNavigationMenuItemLeadingAction
                  aria-label="Edit valuation version"
                  isVisible
                  onClick={handleEditClick}>
                  <EditOutlinedIcon fontSize="inherit" />
                </PrimaryNavigationMenuItemLeadingAction>
              ) : undefined}
              isActive={isSelected}
              key={version.id ?? version.slug}
              onClick={() => handleSelect(version.slug ?? '')}>
              {getVersionLabel(version)}
            </PrimaryNavigationMenuItem>
          );
        })}
        {visibleOptions.length === 0 && (
          <PrimaryNavigationMenuItem disabled tone="muted">
            No versions found
          </PrimaryNavigationMenuItem>
        )}
        {hasMoreOptions && (
          <PrimaryNavigationMenuShowMoreItem
            count={hiddenOptionCount}
            itemLabelPlural="versions"
            itemLabelSingular="version"
            onClick={() => setShowAllOptions(true)}
            variant="more"
          />
        )}
        {canShowFewerOptions && (
          <PrimaryNavigationMenuShowMoreItem
            itemLabelPlural="versions"
            itemLabelSingular="version"
            onClick={() => setShowAllOptions(false)}
            variant="fewer"
          />
        )}
        {onCreateVersion && (
          <PrimaryNavigationMenuAction
            disabled={isCreateVersionDisabled}
            icon={<AddIcon />}
            onClick={handleCreateVersion}>
            {SAVE_AS_NEW_VERSION_LABEL}
          </PrimaryNavigationMenuAction>
        )}
      </PrimaryNavigationMenu>
    </>
  );
};

export default ValuationVersionSelector;
